import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    CheckCircle2, XCircle, Clock, User, Hash, Shield, MapPin, Image as ImageIcon,
    Calendar,
    Car,
    Phone,
    Mail,
    Eye,
    Landmark,
    CreditCard,
    Pencil,
} from 'lucide-react';
import moment from 'moment';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { StatusBadge } from '@/components/common/StatusBadge';
import { SectionCard } from '@/components/common/SectionCard';
import { DetailItem } from '@/components/common/DetailItem';
import { NoDataFound } from '@/components/common/NoDataFound';
import { ReviewCard } from '@/components/common/ReviewCard';
import Button from '@/components/common/Button';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import ViewDocumentModal, { DocumentItem } from '@/components/common/ViewDocumentModal';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { formatDateTime, showToastNotification } from '@/utility/helper';
import { DriverData, DriverDetailResponse } from '@/interfaces/Driver/driver.interface';
import FormField from '@/components/common/FormField';
import { useActiveRole } from '@/hooks/useActiveRole';
import { DriverDeliveredOrders } from './DriverDeliveredOrders';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';

export const DriverDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { prefix } = useActiveRole();
    const { canApprovedReject, canEdit } = usePermissions();
    const [driverData, setDriverData] = useState<DriverData | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionStatus, setActionStatus] = useState<"approve" | "reject" | null>(null);
    const [reason, setReason] = useState("");
    const [docModalOpen, setDocModalOpen] = useState(false);
    const [docModalIndex, setDocModalIndex] = useState(0);
    const fetchDriver = async () => {
        setLoading(true);
        try {
            const response = await callApi<DriverDetailResponse>(
                `${API_URL}${API_ENDPOINT.user}/detail/${id}`,
                { method: 'GET' }
            );
            setDriverData(response?.data);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchDriver();
        }
    }, [id]);

    const handleStatusAction = async () => {
        if (!actionStatus || !driverData) return;

        if (actionStatus === 'reject' && !reason.trim()) {
            showToastNotification('danger', 'Please provide a reason for rejection.');
            return;
        }

        console.log(isVerified);

        setActionLoading(true);
        try {
            const isVerified = actionStatus === 'approve';
            await callApi(
                `${API_URL}${API_ENDPOINT.user}/verify-driver`,
                {
                    method: 'PATCH',
                    data: {
                        userId: id,
                        isVerified: isVerified,
                        reason: actionStatus === 'reject' ? reason : "Approved by administrator"
                    },
                }
            );

            setDriverData(prev => prev ? { ...prev, isVerified: isVerified } : null);
            showToastNotification(
                'success',
                `Driver has been ${isVerified ? 'verified' : 'rejected'} successfully.`
            );
            setActionStatus(null);
            setReason("");
            fetchDriver();
        } catch (error) {
            ThrowError(error);
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </Layout>
        );
    }

    if (!driverData) {
        return (
            <Layout>
                <NoDataFound
                    title="Driver Not Found"
                    description="The driver you are looking for does not exist or may have been removed."
                    onAction={() => navigate(-1)}
                />
            </Layout>
        );
    }

    const isVerified = driverData.isVerified;
    // console.log(isVerified);

    return (
        <Layout>
            <div className="space-y-8 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">

                {/* ── Header ── */}
                <MainHeading
                    title="Driver details"
                    description={
                        <div className="flex items-center gap-2 text-muted-foreground/80 mt-1">
                            <Calendar className="w-3.5 h-3.5" />
                            <span className="text-sm">Registered {formatDateTime(driverData?.createdAt)}</span>
                        </div>
                    }
                    showBack
                    onBack={() => navigate(-1)}
                    actionLabel={canEdit(RESOURCES.DRIVER) && "Edit Profile"}
                    actionIcon={canEdit(RESOURCES.DRIVER) && <Pencil className="w-4 h-4" />}
                    onAction={() => navigate(prefix + '/drivers/edit/' + id)}
                />

                {canApprovedReject(RESOURCES.DRIVER) && <div>
                    {/* ── Approve / Reject Banner (not yet verified) ── */}
                    {!isVerified && (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4">
                            <div className="flex items-start gap-3">
                                <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-bold text-amber-800">Pending Approval</p>
                                    <p className="text-xs text-amber-600 mt-0.5">
                                        This driver has submitted their profile and is awaiting review.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                <Button
                                    variant="outline"
                                    className="border-red-300 text-red-700 hover:bg-red-50 gap-2"
                                    onClick={() => setActionStatus("reject")}
                                    leftIcon={<XCircle className="w-4 h-4" />}
                                >
                                    Reject
                                </Button>
                                <Button
                                    variant="outline"
                                    className="border-green-300 text-green-700 hover:bg-green-50 gap-2"
                                    onClick={() => setActionStatus("approve")}
                                    leftIcon={<CheckCircle2 className="w-4 h-4" />}
                                >
                                    Approve
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* ── Already approved: show Re-reject option ── */}
                    {isVerified && (
                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-2xl px-6 py-4">
                            <div className="flex items-center gap-3">
                                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                                <div>
                                    <p className="text-sm font-bold text-green-800">Driver Approved & Active</p>
                                    <p className="text-xs text-green-600 mt-0.5">This driver can accept and fulfill delivery orders.</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>}

                {/* ── Main Grid ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

                    {/* Left — 2 columns */}
                    <div className="lg:col-span-2 space-y-8">

                        {/* Personal Info */}
                        <SectionCard title="Personal Information" icon={<User />}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-1">
                                <DetailItem
                                    label="Full Name"
                                    value={driverData?.fullName}
                                    icon={<User className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                                <DetailItem
                                    label="Email Address"
                                    value={driverData?.email}
                                    icon={<Mail className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                                <DetailItem
                                    label="Phone Number"
                                    value={driverData?.phone || '—'}
                                    icon={<Phone className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                                {/* <DetailItem
                                    label="License Number"
                                    value={driverData?.licenseNumber || '—'}
                                    icon={<FileText className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                /> */}
                            </div>
                        </SectionCard>

                        {/* Vehicle Info */}
                        <SectionCard title="Vehicle Information" icon={<Car />}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-1">
                                <DetailItem
                                    label="Vehicle Type"
                                    value={driverData?.driverInfo?.vehicleType || '—'}
                                    icon={<Car className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                                <DetailItem
                                    label="Vehicle Number"
                                    value={driverData?.driverInfo?.vehicleNumber || '—'}
                                    icon={<Hash className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                                <DetailItem
                                    label="Vehicle Color"
                                    value={driverData?.driverInfo?.vehicleColor || '—'}
                                    icon={<Shield className="w-4 h-4" />}
                                    className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                />
                            </div>
                        </SectionCard>

                        {/* Wallet / Bank Info */}
                        {driverData?.wallet && (
                            <SectionCard title="Bank Account Details" icon={<Landmark />}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-1">
                                    <DetailItem
                                        label="Bank Name"
                                        value={driverData.wallet.bankName || '—'}
                                        icon={<Landmark className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Account Number"
                                        value={driverData.wallet.accountNumber || '—'}
                                        icon={<Hash className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Account Type"
                                        value={driverData.wallet.accountType || '—'}
                                        icon={<CreditCard className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Branch Name"
                                        value={driverData.wallet.branchName || '—'}
                                        icon={<MapPin className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Branch Code"
                                        value={driverData.wallet.branchCode || '—'}
                                        icon={<Hash className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                </div>
                            </SectionCard>
                        )}

                        {/* Assigned Zones */}
                        {driverData?.driverZones && driverData.driverZones.length > 0 && (
                            <SectionCard title="Assigned Operational Zones" icon={<MapPin />}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {driverData.driverZones.map((dz) => (
                                        dz.zone && (
                                            <div key={dz.id} className="bg-secondary/5 rounded-2xl p-4 border border-border/50 flex flex-col gap-1 hover:border-primary/20 transition-all">
                                                <div className="flex items-center justify-between mb-1">
                                                    <h5 className="font-semibold text-foreground text-sm">{dz.zone.name}</h5>
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${dz.zone.status === 'active' || dz.zone.status === 'ACTIVE' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'} uppercase`}>
                                                        {dz.zone.status}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-muted-foreground line-clamp-1">{dz.zone.description || 'No description provided'}</p>
                                                <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-border/50 text-[11px] text-muted-foreground">
                                                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {dz.zone.city || '—'}</span>
                                                    <span>•</span>
                                                    <span>{dz.zone.area || '—'}</span>
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </div>
                            </SectionCard>
                        )}

                        {/* Addresses */}
                        {driverData?.addresses && driverData.addresses.length > 0 && (
                            <SectionCard title="Registered Addresses" icon={<MapPin />}>
                                <div className="space-y-6">
                                    {driverData.addresses.map((addr) => (
                                        <div key={addr.id} className="rounded-2xl border border-border/50 bg-secondary/5 p-6 space-y-4">
                                            {/* Address header */}
                                            <div className="flex items-center gap-3 pb-2 border-b border-border/40">
                                                <div className="p-2 bg-primary/10 rounded-xl">
                                                    <MapPin className="w-4 h-4 text-primary" />
                                                </div>
                                                <h4 className="font-bold text-foreground capitalize">
                                                    {addr.label || addr.addressType}
                                                </h4>
                                            </div>

                                            {/* All address fields in a responsive grid */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {addr.fullName && (
                                                    <DetailItem label="Full Name" value={addr.fullName}
                                                        icon={<User className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.phoneNumber && (
                                                    <DetailItem label="Phone Number" value={addr.phoneNumber}
                                                        icon={<Phone className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.houseNumber && (
                                                    <DetailItem label="House Number" value={addr.houseNumber}
                                                        icon={<Hash className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.streetNumber && (
                                                    <DetailItem label="Street Number" value={addr.streetNumber}
                                                        icon={<Hash className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.streetName && (
                                                    <DetailItem label="Street Name" value={addr.streetName}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.addressLine && (
                                                    <DetailItem label="Address Line" value={addr.addressLine}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.suburb && (
                                                    <DetailItem label="Suburb" value={addr.suburb}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.city && (
                                                    <DetailItem label="City" value={addr.city}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.province && (
                                                    <DetailItem label="Province" value={addr.province}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.postalCode && (
                                                    <DetailItem label="Postal Code" value={addr.postalCode}
                                                        icon={<Hash className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.country && (
                                                    <DetailItem label="Country" value={addr.country}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.companyName && (
                                                    <DetailItem label="Company Name" value={addr.companyName}
                                                        icon={<Landmark className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.department && (
                                                    <DetailItem label="Department" value={addr.department}
                                                        icon={<User className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.unit && (
                                                    <DetailItem label="Unit" value={addr.unit}
                                                        icon={<Hash className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.buildingName && (
                                                    <DetailItem label="Building Name" value={addr.buildingName}
                                                        icon={<Landmark className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.landmark && (
                                                    <DetailItem label="Landmark" value={addr.landmark}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all" />
                                                )}
                                                {addr.deliveryInstructions && (
                                                    <DetailItem label="Delivery Instructions" value={addr.deliveryInstructions}
                                                        icon={<MapPin className="w-4 h-4" />}
                                                        className="bg-background/60 rounded-xl p-3 border border-transparent hover:border-primary/10 transition-all sm:col-span-2" />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </SectionCard>
                        )}

                        {/* Documents & Media */}
                        {driverData?.media && driverData.media.length > 0 && (() => {
                            const docItems: DocumentItem[] = driverData.media.map((item) => ({
                                id: item.id,
                                url: `${API_URL}/${item.url}`,
                                label: item.referenceType,
                                subtitle: item.referenceType,
                                uploadedAt: item.createdAt,
                            }));

                            return (
                                <SectionCard title="Verification Documents" icon={<ImageIcon />}>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-1">
                                        {docItems.map((doc, idx) => (
                                            <button
                                                key={doc.id}
                                                onClick={() => { setDocModalIndex(idx); setDocModalOpen(true); }}
                                                className="group relative overflow-hidden rounded-2xl border border-border/50 bg-secondary/5 transition-all hover:border-primary/40 hover:shadow-md text-left w-full cursor-pointer"
                                            >
                                                {/* Thumbnail */}
                                                <div className="aspect-[4/3] overflow-hidden rounded-t-2xl bg-muted/20">
                                                    <img
                                                        src={doc.url}
                                                        alt={doc.label}
                                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    />
                                                </div>

                                                {/* Hover overlay */}
                                                <div className="absolute inset-0 rounded-2xl bg-primary/0 group-hover:bg-primary/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                    <div className="bg-white/90 rounded-full p-2.5 shadow-lg">
                                                        <Eye className="w-5 h-5 text-primary cursor-pointer" />
                                                    </div>
                                                </div>

                                                {/* Label */}
                                                <div className="p-3">
                                                    <p className="text-[11px] font-bold uppercase tracking-widest text-primary/70 truncate">
                                                        {doc.label.replace(/_/g, ' ')}
                                                    </p>
                                                    <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
                                                        <Calendar className="w-3 h-3" />
                                                        {formatDateTime(doc.uploadedAt ?? '')}
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>

                                    {/* View Document Modal */}
                                    <ViewDocumentModal
                                        open={docModalOpen}
                                        onOpenChange={setDocModalOpen}
                                        documents={docItems}
                                        initialIndex={docModalIndex}
                                    />
                                </SectionCard>
                            );
                        })()}

                        {/* Delivered Orders & Earnings */}
                        {id && (
                            <DriverDeliveredOrders driverId={id} />
                        )}
                    </div>


                    {/* Right Sidebar */}
                    <div className="space-y-8 sticky top-6">

                        {/* Status Overview */}
                        <SectionCard title="Account Status" icon={<Shield />}>
                            <div className="grid grid-cols-1 gap-y-6">
                                <DetailItem
                                    label="Verify Status"
                                    value={<StatusBadge status={driverData?.isVerified ? "verified" : "not_Verified"} />}
                                    icon={<Shield />}
                                />
                                <DetailItem
                                    label="Online Status"
                                    value={<StatusBadge status={driverData?.isOnline ? "online" : "offline"} />}
                                    icon={<Shield />}
                                />
                                <div className="pt-2 border-t border-border/40">
                                    <DetailItem
                                        label="Registered On"
                                        value={formatDateTime(driverData?.createdAt)}
                                        icon={<Calendar />}
                                    />
                                </div>
                            </div>
                        </SectionCard>

                        {/* Reviews & Ratings Card */}
                        {id && (
                            <ReviewCard
                                targetType="on_driver"
                                targetId={id}
                                title="Driver Reviews & Ratings"
                            />
                        )}


                    </div>
                </div>
            </div>

            {/* ── Approve Confirmation Modal ── */}
            <ConfirmationModal
                open={actionStatus === "approve"}
                onOpenChange={(open) => !open && setActionStatus(null)}
                onConfirm={handleStatusAction}
                loading={actionLoading}
                type="success"
                title="Approve Driver?"
                message={`Are you sure you want to approve ${driverData?.fullName}? They will be able to start accepting delivery orders immediately.`}
                confirmText="Yes, Approve"
                cancelText="Cancel"
                size='lg'
            />

            {/* ── Reject Confirmation Modal ── */}
            <ConfirmationModal
                open={actionStatus === "reject"}
                onOpenChange={(open) => {
                    if (!open) {
                        setActionStatus(null);
                        setReason("");
                    }
                }}
                onConfirm={handleStatusAction}
                loading={actionLoading}
                type="danger"
                title="Reject Driver?"
                message={`Are you sure you want to reject ${driverData?.fullName}? Their access will be revoked and they will not be able to accept orders.`}
                confirmText="Yes, Reject"
                cancelText="Cancel"
                size='lg'
            >
                <div className="mt-4">
                    <FormField
                        type="textarea"
                        label="Rejection Reason"
                        placeholder="Please explain why this driver is being rejected..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        required
                        rows={4}
                    />
                </div>
            </ConfirmationModal>


        </Layout>
    );
};
